import { FooterLink } from "data/types";
import React from "react";
import Logo from "shared/Logo/Logo";
import { useData } from "data/data-provider";

export interface WidgetFooterMenu {
  id: string;
  title: string;
  menus: FooterLink[];
}

const Footer: React.FC = () => {
  const { socialItems, address, contactItems, footerItems, footer } = useData();

  if (
    (!socialItems || socialItems.length === 0) &&
    (!address || address.length === 0) &&
    (!contactItems || contactItems.length === 0) &&
    (!footerItems || footerItems.length === 0)
  ) {
    return <div />;
  }

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "columns"  → 4-col grid (default, preserves the previous render)
  //   "centered" → centered single column: logo + info + nav row + contact +
  //                socials, all centered. Reuses Design A's own footer classes
  //                so the centered variant matches Design A styling.
  const layoutVariant: "columns" | "centered" =
    footer?.layout_variant === "centered" ? "centered" : "columns";

  if (layoutVariant === "centered") {
    return (
      <div className="nc-Footer relative py-8 border-t border-secondary-100 bg-bg-color">
        <div className="container flex flex-col items-center text-center gap-8">
          <div>
            <Logo />
          </div>

          {/* Footer tagline — the footer Section's own `description`, set in
              the FMS "Footer tagline" field. Rich text (Quill HTML). */}
          {footer?.description && (
            <p
              className="max-w-xl text-text-color text-xs sm:text-sm leading-relaxed [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
              dangerouslySetInnerHTML={{ __html: footer.description }}
            />
          )}

          {/* Info lines — footerItems without a heading read as plain info
              lines under the brand (same convention Design B uses). */}
          {footerItems.some((item: any) => !item.heading) && (
            <div className="max-w-xl text-sm space-y-3">
              {footerItems
                .filter((item: any) => !item.heading)
                .map((item: any, index: any) => (
                  <p
                    key={item.id || index}
                    className="text-text-color break-words text-xs sm:text-sm"
                  >
                    {item.description}
                  </p>
                ))}
            </div>
          )}

          {/* Centered horizontal nav row — footerItems WITH a heading. */}
          {footerItems.some((item: any) => item.heading) && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {footerItems
                .filter((item: any) => item.heading)
                .map((item: any, index: any) => (
                  <a
                    key={item.id || index}
                    className="text-text-color hover:underline transition text-xs sm:text-sm"
                    href={item.description}
                  >
                    {item.heading}
                  </a>
                ))}
            </div>
          )}

          {/* Centered contact row. */}
          {contactItems.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {contactItems.map((item: any, index: any) => (
                <div key={item.id || index}>
                  {item.heading ? (
                    <a
                      className="text-text-color hover:underline transition text-xs sm:text-sm"
                      href={item.description}
                    >
                      {item.heading}
                    </a>
                  ) : (
                    <p className="text-text-color break-words text-xs sm:text-sm">
                      {item.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Centered address row(s). */}
          {address.length > 0 && (
            <div className="text-sm space-y-3">
              {address.map((item: any, index: any) => (
                <p
                  key={item.id || index}
                  className="text-text-color break-words text-xs sm:text-sm"
                >
                  {item.description}
                </p>
              ))}
            </div>
          )}

          {/* Centered social row — same look as the columns layout. */}
          {socialItems.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {socialItems.map((item: any, index: any) => (
                <a
                  key={item.id || index}
                  href={item.description}
                  className="underline font-bold text-sm hover:opacity-80 transition"
                >
                  {item.heading.toUpperCase()}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="nc-Footer relative py-8 border-t border-secondary-100 bg-bg-color">
      <div className="container grid grid-cols-1 gap-y-10 gap-x-4 sm:gap-x-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-x-8">
        <div className="col-span-1 md:col-span-2 lg:col-span-1">
          <div className="flex flex-col gap-5">
            <div>
              <Logo />
            </div>
            {/* Footer tagline — the footer Section's own `description`, set in
                the FMS "Footer tagline" field. Rich text (Quill HTML). */}
            {footer?.description && (
              <p
                className="text-text-color text-xs sm:text-sm leading-relaxed [&_p]:m-0 [&_p]:inline [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                dangerouslySetInnerHTML={{ __html: footer.description }}
              />
            )}
            <div className="grid grid-cols-2 gap-3">
              {socialItems.map((item: any, index: any) => (
                <a
                  key={item.id || index}
                  href={item.description}
                  className="underline font-bold text-sm hover:opacity-80 transition"
                >
                  {item.heading.toUpperCase()}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="text-sm">
          <h2 className="font-bold text-text-color mb-5">Address</h2>
          <div className="space-y-3">
            {address.map((item: any, index: any) => (
              <p key={item.id || index} className="text-text-color break-words text-xs sm:text-sm">
                {item.description}
              </p>
            ))}
          </div>
        </div>
        <div className="text-sm">
          <h2 className="font-bold text-text-color mb-5">Contact Us</h2>
          <div className="space-y-3">
            {contactItems.map((item: any, index: any) => (
              <div key={item.id || index}>
                {item.heading ? (
                  <a
                    className="text-text-color hover:underline transition text-xs sm:text-sm"
                    href={item.description}
                  >
                    {item.heading}
                  </a>
                ) : (
                  <p className="text-text-color break-words text-xs sm:text-sm">
                    {item.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="text-sm">
          <h2 className="font-bold text-text-color mb-5">Links</h2>
          <div className="space-y-3">
            {footerItems.map((item: any, index: any) => (
              <div key={item.id || index}>
                {item.heading ? (
                  <a
                    className="text-text-color hover:underline transition text-xs sm:text-sm"
                    href={item.description}
                  >
                    {item.heading}
                  </a>
                ) : (
                  <p className="text-text-color break-words text-xs sm:text-sm">
                    {item.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Footer;
